import { portalText } from "../i18n/portal.js";
/**
 * Caller protection in History, Live and Analytics — plan-029 unit **WAPP-HIST1** (design §40.12, §40.13.1, §40.13.2,
 * §40.19.6; FR-CP-4, FR-CP-13; AC-10; ADR 0081, proposed), over API-READ1's fields:
 *
 *   GET /v1/conversations                 `?end_reason=` a comma list of the seven reasons below (History's filter)
 *   GET /v1/conversations/{id}            `may_block` — whether "Block this number" is offered
 *   GET /v1/agents/{id}/analytics         `refused_by_reason` (the five refusals) and `ended_by_reason` (the two ends)
 *   conversation_event `party_protection` `detail { code, stage, reason, line_played, window }` (codes only)
 *
 * NEVER A NUMBER. Nothing here reads, keeps or shows a caller's number or digest: "Block this number" (WAPP-PROT's
 * `blockConversationNumber`) names the conversation, and the server reads the digest from it (design §40.5). What comes
 * back names the last three digits at most.
 *
 * THE FILTER RIDES ON THE OUTCOME CHIP. History's typed URL already carries one chip value (`outcome`), which may be a
 * code rather than a template's word (plan-018's `message_taken`). The two caller-protection chips are codes of the same
 * kind — `party_refused` (the five refusals) and `party_ended` (the two ends) — and one end reason itself (a link from a
 * notice or a bookmark) is offered as its own chip, so a refused conversation is one shareable link. No template's
 * outcome word is a snake_case code, so none collides.
 *
 * THE FIELDS ARE OPTIONAL on the views because the in-memory gateway builds its own literals; the api gateway always
 * fills them.
 */
import {
  PARTY_PROTECTION_END_REASON,
  PARTY_REFUSAL_END_REASON,
  type PartyProtectionEventDetail,
} from "@wetalk/domain";

import { readBoolean, readRecord, readWholeNumber } from "./decode.js";
import type { BlockedNumberAddedView } from "./slice/blocked-number.js";

/** The five refusals: refused before answering, never billed (design §40.12.1). */
export type PartyRefusalEndReason = (typeof PARTY_REFUSAL_END_REASON)[number];
/** All seven: the five refusals and the two ends of an answered conversation. */
export type PartyProtectionEndReason = (typeof PARTY_PROTECTION_END_REASON)[number];
/** The two ends of an answered, billed conversation. */
export type PartyEndedReason = Exclude<PartyProtectionEndReason, PartyRefusalEndReason>;

export const PARTY_ENDED_REASON: readonly PartyEndedReason[] = PARTY_PROTECTION_END_REASON.filter(
  (one): one is PartyEndedReason => !(PARTY_REFUSAL_END_REASON as readonly string[]).includes(one),
);

/** History's chip for the five refusals. */
export const PARTY_REFUSED_FILTER = "party_refused";
/** History's chip for the two ends. */
export const PARTY_ENDED_FILTER = "party_ended";

/** Analytics' `refused_by_reason`: every refusal present, 0 when none. */
export type PartyRefusedByReasonView = Readonly<Record<PartyRefusalEndReason, number>>;
/** Analytics' `ended_by_reason`: both ends present, 0 when none. */
export type PartyEndedByReasonView = Readonly<Record<PartyEndedReason, number>>;

declare module "./gateway.js" {
  interface PastConversationView {
    /**
     * plan-029: "Block this number" is offered — a phone conversation whose caller is known, read by a person who may
     * change what the workspace's VoiceAgents answer. Absent (the in-memory gateway): not offered.
     */
    readonly mayBlock?: boolean;
  }
  interface VoiceAgentAnalyticsView {
    /** plan-029: conversations caller protection refused in the window, by reason (none was answered or billed). */
    readonly refusedByReason?: PartyRefusedByReasonView;
    /** plan-029: answered conversations caller protection ended in the window, by reason (billed to the end). */
    readonly endedByReason?: PartyEndedByReasonView;
  }
}

export function isPartyProtectionEndReason(value: string | null | undefined): value is PartyProtectionEndReason {
  return value !== null && value !== undefined && (PARTY_PROTECTION_END_REASON as readonly string[]).includes(value);
}

export function isPartyRefusalEndReason(value: string | null | undefined): value is PartyRefusalEndReason {
  return value !== null && value !== undefined && (PARTY_REFUSAL_END_REASON as readonly string[]).includes(value);
}

/** "Refused: blocked number", "Ended: the caller said nothing" — History's words for one end reason. */
export function partyProtectionReasonText(reason: PartyProtectionEndReason): string {
  switch (reason) {
    case "party_blocked":
      return portalText("callerProtection", "history.refused_blocked");
    case "party_backstop_rejected":
      return portalText("callerProtection", "history.refused_backstop");
    case "party_limit_reached":
      return portalText("callerProtection", "history.refused_limit");
    case "withheld_party_refused":
      return portalText("callerProtection", "history.refused_withheld");
    case "party_not_listed":
      return portalText("callerProtection", "history.refused_not_listed");
    case "silent_start_ended":
      return portalText("callerProtection", "history.ended_silent_start");
    case "longest_conversation_reached":
      return portalText("callerProtection", "history.ended_longest");
  }
}

/** "Blocked number", "The caller said nothing" — one reason's short words, as Analytics lists them under a heading. */
export function partyProtectionReasonShortText(reason: PartyProtectionEndReason): string {
  switch (reason) {
    case "party_blocked":
      return portalText("callerProtection", "reason.party_blocked");
    case "party_backstop_rejected":
      return portalText("callerProtection", "reason.party_backstop_rejected");
    case "party_limit_reached":
      return portalText("callerProtection", "reason.party_limit_reached");
    case "withheld_party_refused":
      return portalText("callerProtection", "reason.withheld_party_refused");
    case "party_not_listed":
      return portalText("callerProtection", "reason.party_not_listed");
    case "silent_start_ended":
      return portalText("callerProtection", "reason.silent_start_ended");
    case "longest_conversation_reached":
      return portalText("callerProtection", "reason.longest_conversation_reached");
  }
}

/**
 * The end reasons a History chip value asks for, or `null` when the value is not one of caller protection's: the two
 * group chips, or one of the seven reasons named on its own.
 */
export function partyProtectionEndReasonsOf(outcome: string): readonly PartyProtectionEndReason[] | null {
  if (outcome === PARTY_REFUSED_FILTER) return PARTY_REFUSAL_END_REASON;
  if (outcome === PARTY_ENDED_FILTER) return PARTY_ENDED_REASON;
  if (isPartyProtectionEndReason(outcome)) return [outcome];
  return null;
}

/** A caller-protection chip as `/v1/conversations` query parameters, or `null` for any other chip value. */
export function partyProtectionHistoryWireOf(outcome: string): Record<string, string> | null {
  const reason = partyProtectionEndReasonsOf(outcome);
  return reason === null ? null : { outcome_state: "any", end_reason: reason.join(",") };
}

/** A chip value's label: "Refused", "Ended by caller protection", or one reason's History words; `null` otherwise. */
export function partyProtectionChipLabelOf(outcome: string): string | null {
  if (outcome === PARTY_REFUSED_FILTER) return portalText("callerProtection", "history.filter_refused");
  if (outcome === PARTY_ENDED_FILTER) return portalText("callerProtection", "history.filter_ended");
  return isPartyProtectionEndReason(outcome) ? partyProtectionReasonText(outcome) : null;
}

/** `may_block` off the conversation's detail. Required on the wire since API-READ1. */
export function mayBlockOfWire(data: Readonly<Record<string, unknown>>, path: string): boolean {
  return readBoolean(data["may_block"], `${path}.may_block`);
}

function countsOfWire<Reason extends string>(
  value: unknown,
  path: string,
  reason: readonly Reason[],
): Readonly<Record<Reason, number>> {
  const one = readRecord(value, path);
  const counted = {} as Record<Reason, number>;
  for (const each of reason) counted[each] = readWholeNumber(one[each], `${path}.${each}`);
  return counted;
}

/** Analytics' `refused_by_reason`, every reason required. */
export function refusedByReasonOfWire(value: unknown, path: string): PartyRefusedByReasonView {
  return countsOfWire(value, path, PARTY_REFUSAL_END_REASON);
}

/** Analytics' `ended_by_reason`, both reasons required. */
export function endedByReasonOfWire(value: unknown, path: string): PartyEndedByReasonView {
  return countsOfWire(value, path, PARTY_ENDED_REASON);
}

/** The sum of a by-reason block. */
export function partyReasonTotalOf(counts: Readonly<Record<string, number>>): number {
  return Object.values(counts).reduce((sum, one) => sum + one, 0);
}

/**
 * One `party_protection` event of "What happened" in plain words (design §40.12.2): the reason, then what the caller
 * heard — "Refused: over the limit · Daily limit · The caller was told why". Codes only arrive, never a number; an
 * unknown code reads as caller protection's refusal rather than raw.
 */
export function partyProtectionEventSentenceOf(detail: Readonly<Record<string, unknown>>): string {
  const code = typeof detail["code"] === "string" ? (detail["code"] as PartyProtectionEventDetail["code"]) : null;
  const reason = typeof detail["reason"] === "string" ? detail["reason"] : null;
  switch (code) {
    case "silent_start_ended":
      return partyProtectionReasonText("silent_start_ended");
    case "longest_conversation_reached":
      return partyProtectionReasonText("longest_conversation_reached");
    case "final_minute_said":
      return portalText("callerProtection", "event.final_minute");
    case "rejected":
    case "refused": {
      const part = [
        isPartyProtectionEndReason(reason) ? partyProtectionReasonText(reason) : portalText("callerProtection", "event.refused"),
      ];
      if (detail["window"] === "hour") part.push(portalText("callerProtection", "event.window_hour"));
      if (detail["window"] === "day") part.push(portalText("callerProtection", "event.window_day"));
      if (code === "rejected" || detail["stage"] === "a") part.push(portalText("callerProtection", "event.stage_a"));
      if (detail["line_played"] === true) part.push(portalText("callerProtection", "event.line_played"));
      if (detail["line_played"] === false) part.push(portalText("callerProtection", "event.line_not_played"));
      return part.join(" · ");
    }
    default:
      return portalText("callerProtection", "event.refused");
  }
}

/**
 * What "Block this number" answered (WAPP-PROT's `blockConversationNumber`), in words: "Number ending …123 is blocked."
 * — or "already blocked", or without digits for a caller known only by what their own phone system sent.
 */
export function conversationNumberBlockTextOf(added: BlockedNumberAddedView): string {
  const lastThree = added.entry.lastThreeDigit;
  if (lastThree === null) {
    return added.created
      ? portalText("callerProtection", "block_this.done_no_digit")
      : portalText("callerProtection", "block_this.already_no_digit");
  }
  return added.created
    ? portalText("callerProtection", "block_this.done", { last_three: lastThree })
    : portalText("callerProtection", "block_this.already", { last_three: lastThree });
}
